import * as React from 'react'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { ImportCustomTablesModalPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import Importer from 'modules/employees/components/Importer'
import OverlimitEmployeesFootNote from 'modules/employees/components/OverlimitEmployees/FootNote'
import { TipLink, Tips } from 'modules/employees/components/Tips'
import type { EmployeeImporterContextProps } from 'modules/employees/lib/employeesImporterContext'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

import useProcessFile from './useProcessFile'

const DOWNLOAD_PATH = '/onboarding_importer/imports_custom_tables'
const CHECK_DOWNLOAD = '/onboarding_importer/download_available_custom_tables'

const TipsComponent = () => {
  const tips = React.useRef([
    i18n.tView('import.tips.before_importing_custom_tables', {
      link: (
        <TipLink
          text={i18n.t('import.tips.configure_custom_tables')}
          href={helpCenterUrl('setup_custom_tables')}
          target="_blank"
        />
      ),
    }),
    i18n.t('import.tips.import_custom_tables_new_values'),
    i18n.t('import.tips.custom_tables_multi_data'),
    i18n.t('import.tips.large_file'),
  ]).current

  return <Tips tips={tips} />
}

interface Props {
  importer: EmployeeImporterContextProps
  onClose: () => void
}

const ImportCustomTablesModalPage = ({ importer, onClose }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()

  const { data } = useQuery(ImportCustomTablesModalPageDocument)

  const api = React.useRef(ApiClient()).current
  const [upgradeModalOpen, setUpgradeModalOpen] = useToggle(false)

  const { running: downloading, performAction: onDownloadRequest } = useAsyncAction(
    async () => {
      const response = await api.get(CHECK_DOWNLOAD)

      if (response.result) {
        window.location.href = buildUrl(DOWNLOAD_PATH)
      } else {
        app.setFlash({
          type: 'correct',
          message: i18n.t('employees.export_template_by_email'),
        })
      }
    },
    {
      successFlash: false,
    }
  )

  const { running, onSubmit } = useProcessFile({
    api,
  })

  const canImportCustomTables = data?.onboardingImporter?._policies.importCustomTables.can ?? false

  const title = i18n.t('employees.custom_tables_import_title')
  const description = i18n.t('custom_tables_import.description')

  if (!canImportCustomTables) return null

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        illustration="group"
        title={title}
        description={description}
        onClose={onClose}
        onSubmit={onSubmit}
        footer={
          <Button disabled={!importer.hasFile || running} submit>
            {i18n.t('import.process_file')}
          </Button>
        }
      >
        <Box gap="s24">
          <Box flexDirection="row" justifyContent="center">
            <Button onClick={onDownloadRequest} disabled={downloading} type="secondary">
              {i18n.t('import.download_custom_tables_template')}
            </Button>
          </Box>
          <Importer importer={importer} />
          <TipsComponent />
          <OverlimitEmployeesFootNote setUpgradeModalOpen={setUpgradeModalOpen} />
        </Box>
      </Modal>
      {upgradeModalOpen && (
        <RequestUpgradeModal
          requestReason={ValidReasons.UpgradeFromLegacy}
          requestSource="custom-tables-importer"
          onClose={onClose}
        />
      )}
    </ExpiredTrialChecker>
  )
}

export default observer(ImportCustomTablesModalPage)
